// Rotas públicas do build principal para pedidos e domínios derivados (vendas, orçamentos,
// ordens de serviço). Os SPAs internos foram migrados para modules/pedidos (e seus submodules
// vendas/orcamentos/ordens). Aqui ficam apenas as rotas public:true de raiz — links e impressões
// abertos por URL ou :href, que precisam responder mesmo sem o submódulo/módulo carregado.
//
// Consolidado: as rotas públicas de vendas, orcamentos e ordens_servicos foram movidas para cá
// (as pastas src/domains/{vendas,orcamentos,ordens_servicos} deixaram de existir). Componentes
// locais: Cobranca, Envio e Pdv/ImpressaoCupom (este vindo do antigo vendas/balcao). O restante
// usa os compartilhados Impressao/Visualizacao/Download.

// ===== Redirecionamentos de rotas antigas → módulo Pedidos =====
// Muitos links e integrações ainda apontam para as rotas planas antigas
// (ex.: /vendas_produtos, /ordens_servicos, /orcamentos_servicos). Os CRUDs
// migraram para src/modules/pedidos (submodules vendas/ordens/orcamentos), que em
// produção são builds SPA separados — por isso o redirect é via location.href
// (recarregamento completo), feito pelo componente RedirecionaModulo a partir de
// meta.destino. Cada prefixo gera 2 rotas: a exata (lista) e a curinga (/*) para
// adicionar/editar/visualizar/etc.
//
// IMPORTANTE: estes redirects são concatenados DEPOIS das rotas public:true abaixo.
// O Vue Router casa na ordem de declaração, então as impressões específicas
// (/vendas_produtos/imprimir_ordem_producao/:id, /devolucoes/imprimir_pdf/:id, ...)
// continuam sendo atendidas pelo build principal e não caem no curinga.
const RedirecionaModulo = () => import('../components/RedirecionaModulo')

const mapaRedirecionamentos = [
    // submodule vendas → /pedidos/vendas
    ['/vendas_produtos', '/pedidos/vendas'],
    ['/vendas_servicos', '/pedidos/vendas'],
    ['/vendas_balcao', '/pedidos/vendas'],
    ['/devolucoes', '/pedidos/vendas'],
    ['/atributos_vendas', '/pedidos/vendas'],
    ['/situacoes_vendas', '/pedidos/vendas'],
    ['/balancas', '/pedidos/vendas'],
    ['/canais_vendas', '/pedidos/vendas'],
    ['/relatorios_vendas', '/pedidos/vendas'],
    ['/relatorios_curvas_abc', '/pedidos/vendas'],
    // submodule ordens → /pedidos/ordens
    ['/ordens_servicos', '/pedidos/ordens'],
    ['/ordens_servicos_painel', '/pedidos/ordens'],
    ['/atributos_ordens_servicos', '/pedidos/ordens'],
    ['/situacoes_ordens_servicos', '/pedidos/ordens'],
    ['/relatorios_ordens_servicos', '/pedidos/ordens'],
    // submodule orcamentos → /pedidos/orcamentos
    ['/orcamentos_produtos', '/pedidos/orcamentos'],
    ['/orcamentos_servicos', '/pedidos/orcamentos'],
    ['/atributos_orcamentos', '/pedidos/orcamentos'],
    ['/situacoes_orcamentos', '/pedidos/orcamentos'],
    ['/relatorios_orcamentos', '/pedidos/orcamentos'],
]

const redirecionamentos = []
mapaRedirecionamentos.forEach((par) => {
    const prefixo = par[0]
    const destino = par[1]
    redirecionamentos.push({ path: prefixo, component: RedirecionaModulo, meta: { public: true, destino } })
    redirecionamentos.push({ path: prefixo + '/*', component: RedirecionaModulo, meta: { public: true, destino } })
})

const routes = [
    // ===== Pedido (cobrança, envio, visualização, XML) =====
    {
        name: 'linkCobranca',
        path: '/cobranca/:hash',
        alias: '/prop/:hash',
        component: () => import('../components/Cobranca'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'linkEnvio',
        path: '/ven/:hash',
        alias: ['/ordem/:hash', '/orc/:hash'],
        component: () => import('../components/Envio'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'visualizarPedido',
        path: '/pedidos/visualizar/:id',
        component: () => import('@/shared/components/ui/Visualizacao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'baixarXmlIdNotaFiscal',
        path: '/nfe/xml/:hash',
        component: () => import('@/shared/components/ui/Download'),
        meta: { public: true }
    },
    {
        name: 'baixarXmlIdNotaFiscalConsumidor',
        path: '/nfce/xml/:hash',
        component: () => import('@/shared/components/ui/Download'),
        meta: { public: true }
    },
    {
        name: 'baixarXmlIdNotaFiscalServico',
        path: '/nfse/xml/:hash',
        component: () => import('@/shared/components/ui/Download'),
        meta: { public: true }
    },
    // ===== Vendas — Balcão (PDV) =====
    {
        name: 'impressaoVendaBalcao',
        path: '/ped/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'impressaoCupomHashVendaBalcao',
        path: '/cupom/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'impressaoCupomIdVendaBalcao',
        path: '/vendas_balcao/imprimir_cupom/:id',
        component: () => import('../components/Pdv/ImpressaoCupom'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'cupomPresenteHashVendaBalcao',
        path: '/balcao_cupom_presente/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'cupomPresenteIdVendaBalcao',
        path: '/vendas_balcao/imprimir_cupom_presente/:id',
        component: () => import('../components/Pdv/ImpressaoCupom'),
        meta: { fullScreen: true, public: true }
    },
    // ===== Vendas — Produtos =====
    {
        name: 'impressaoVendaProduto',
        path: '/venda/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'cupomPresenteHashVendaProduto',
        path: '/vendas_cupom_presente/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'impressaoOrdemProducao',
        path: '/vendas_produtos/imprimir_ordem_producao/:id',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    // ===== Vendas — Serviços =====
    {
        name: 'impressaoVendaServico',
        path: '/pedido/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'impressaoOrdemServicoVenda',
        path: '/vendas_servicos/imprimir_ordem_servico/:id',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    // ===== Orçamentos =====
    {
        name: 'impressaoOrcamentoProduto',
        path: '/proposta/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'impressaoOrcamentoServico',
        path: '/orcamento/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    // ===== Ordens de serviço =====
    {
        name: 'impressaoOrdemServico',
        path: '/os/:hash',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'ordemProducaoOrdemServicoImpressao',
        path: '/ordens_servicos/imprimir_ordem_producao/:id',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    {
        name: 'imprimirEtiquetaOrdemServico',
        path: '/ordens_servicos/imprimir_etiqueta/:id',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    // ===== Devoluções =====
    {
        name: 'impressaoDevolucao',
        path: '/devolucoes/imprimir_pdf/:id',
        component: () => import('@/shared/components/ui/Impressao'),
        meta: { fullScreen: true, public: true }
    },
    // Curingas de redirect — sempre por último (ver nota no topo do arquivo)
    ...redirecionamentos,
]

export default routes
